import { useParams, Link } from 'react-router-dom';
import { getMovieById, getMoviesByCategory, getCategoryName } from '@/mocks/movies';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NotFound from '@/pages/NotFound';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(parseInt(id, 10)) : undefined;

  if (!movie) {
    return <NotFound />;
  }

  const relatedMovies = getMoviesByCategory(movie.categorySlug)
    .filter((m) => m.id !== movie.id)
    .slice(0, 4);
  const categoryName = getCategoryName(movie.categorySlug);

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-500 transition-colors">
                {categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-800 font-medium">{movie.title}</span>
            </nav>
          </div>
        </div>

        {/* Movie Hero */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              {/* Poster */}
              <div className="flex-shrink-0 w-full sm:w-64 md:w-72 mx-auto lg:mx-0">
                <div className="rounded-lg overflow-hidden border border-background-200/60">
                  <img
                    src={movie.posterUrl}
                    alt={`${movie.title} 高清电影在线看`}
                    title={`${movie.title} 天堂影院免费在线观看 高清不缓冲`}
                    className="w-full object-cover object-top"
                    style={{ aspectRatio: '2/3' }}
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-400 mb-4">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-md whitespace-nowrap">
                    <i className="ri-star-fill text-accent-500"></i>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-secondary-500 bg-secondary-100 px-2.5 py-0.5 rounded-md whitespace-nowrap">
                    {movie.category}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>
                    {(movie.views / 10000).toFixed(0)}万次观看
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-foreground-400 whitespace-nowrap w-14">导演：</span>
                    <span className="text-sm text-foreground-800">{movie.director}</span>
                  </div>
                  {movie.cast.length > 0 && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap w-14">主演：</span>
                      <span className="text-sm text-foreground-800">
                        {movie.cast.join(' / ')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm text-foreground-400 whitespace-nowrap w-14">类型：</span>
                    <Link
                      to={`/category/${movie.categorySlug}`}
                      className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
                    >
                      {movie.category}
                    </Link>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mb-6">
                  <button className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-300 whitespace-nowrap text-sm">
                    <i className="ri-play-circle-line mr-2"></i>
                    立即播放
                  </button>
                  <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all duration-300 whitespace-nowrap text-sm border border-background-200/60">
                    <i className="ri-heart-line mr-2"></i>
                    收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all duration-300 whitespace-nowrap text-sm border border-background-200/60">
                    <i className="ri-share-forward-line mr-2"></i>
                    分享
                  </button>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed">
                  {movie.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Synopsis */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-5">
              剧情简介
            </h2>
            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
              {movie.longDescription.split('\n').map((para, idx) => (
                <p key={idx} className="mb-3 text-sm">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="py-8 md:py-10 bg-background-50">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
                >
                  更多{categoryName}电影
                  <i className="ri-arrow-right-s-line ml-1"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/movie/${m.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-300/50 transition-all duration-300"
                  >
                    <div className="relative overflow-hidden" style={{ aspectRatio: '2/3' }}>
                      <img
                        src={m.posterUrl}
                        alt={`${m.title} 高清电影在线看`}
                        title={`${m.title} 天堂影院免费在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-sm font-semibold text-background-50 line-clamp-1">
                          {m.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          <span className="text-xs text-background-100/80">{m.rating}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            name: movie.title,
            description: movie.description,
            director: {
              "@type": "Person",
              name: movie.director,
            },
            actor: movie.cast.map((name) => ({
              "@type": "Person",
              name,
            })),
            dateCreated: String(movie.year),
            genre: [movie.category],
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(movie.rating),
              bestRating: "10",
              ratingCount: Math.floor(movie.views / 1000),
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://www.ttcinema.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: categoryName,
                item: `https://www.ttcinema.com/category/${movie.categorySlug}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}